import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StickyNote, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function ReconciliationNoteButton({ entityType, entityId, label }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  const load = () => {
    api.get("/reconciliation-notes", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setNotes(r.data)).catch(() => {});
  };

  useEffect(() => { if (open) load(); }, [open]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/reconciliation-notes", { entity_type: entityType, entity_id: entityId, note: text.trim() });
      setText("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/reconciliation-notes/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <>
      <button
        data-testid={`reconciliation-note-button-${entityId}`}
        onClick={() => setOpen(true)}
        className="relative text-[#4B5563] hover:text-[#00823B]"
        title="Reconciliation notes"
      >
        <StickyNote className="h-4 w-4" />
        {notes.length > 0 && <span className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 rounded-full bg-[#76C121] text-white text-[9px] flex items-center justify-center">{notes.length}</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reconciliation-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reconciliation Notes{label ? ` \u2014 ${label}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="reconciliation-note-list">
            {notes.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No notes yet.</p>}
            {notes.map((n) => (
              <div key={n.id} data-testid={`reconciliation-note-${n.id}`} className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-2.5 text-sm">
                <p className="text-[#1C1C1E]">{n.note}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-[#4B5563]">{n.created_by} · {new Date(n.created_at).toLocaleDateString()}</span>
                  {(user?.role === "admin" || n.created_by_id === user?.id) && (
                    <button data-testid={`reconciliation-note-delete-${n.id}`} onClick={() => deleteNote(n.id)} className="text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Textarea data-testid="reconciliation-note-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a reconciliation note..." rows={2} />
          <DialogFooter>
            <Button onClick={addNote} disabled={submitting || !text.trim()} data-testid="reconciliation-note-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
              {submitting ? "Saving..." : "Add Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
